import { Alert } from '@mui/material'
import upperFirst from 'lodash/upperFirst'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'temporal-polyfill/global'

import {
  PolicyName,
  Provider,
  Offense_PolicyTypeEnum,
  Offense_DeviceTypeEnum,
  Policy_StatusEnum,
} from 'src/api/gen/api'
import {
  getEnumEntries,
  getPolicyKindEntries,
  getPolicyStatusEntries,
  getProviderEntries,
} from 'src/commons/filters/utils'
import { sortObjectsByLabel } from 'src/commons/helpers'
import { enumsTranslationKeys, withFormattedCount } from 'src/commons/i18nEnums'
import { ITemporalRange } from 'src/commons/temporalHelpers'
import { VIOLATION_MAP_POLICY_KINDS } from 'src/components/Offenses'
import { useAgencySettings, useEnabledPolicyKinds } from 'src/layout/AgencySettings'
import CheckboxListFilter from 'src/muiv5/components/commons/SidePanel/Filters/CheckboxListFilter/CheckboxListFilter'
import TimeRangeFilterPro from 'src/muiv5/components/commons/SidePanel/Filters/TimeRangeFilter/TimeRangeFilterPro'
import { SidePanel } from 'src/muiv5/components/commons/SidePanel/SidePanel'

export interface IViolationMapFilters {
  dateRange: ITemporalRange
  deviceTypes: Offense_DeviceTypeEnum[]
  eventType: string[]
  include_abnormals: boolean
  policyId: string[]
  policyKind: Offense_PolicyTypeEnum[]
  policyStatus: Exclude<
    Policy_StatusEnum,
    Policy_StatusEnum.draft | Policy_StatusEnum.upcoming | Policy_StatusEnum.cancelled
  >[]
  provider: string[]
}

interface IProps {
  defaultFilters: IViolationMapFilters
  displayProviderColor?: boolean
  initialFilters: IViolationMapFilters
  policies: PolicyName[]
  providers: Provider[]
  violationsFailed: boolean
  violationsTotalCount: number
  violationsTruncated: boolean
}

const ViolationMapFilterPanel = forwardRef(function (props: IProps, ref) {
  const { t } = useTranslation()
  const agency_settings = useAgencySettings()

  const [filters, setFilters] = useState(props.initialFilters)
  useImperativeHandle(ref, () => filters, [filters])

  const enabledPolicyKinds = useEnabledPolicyKinds()
  const violationMapPolicyKinds = enabledPolicyKinds.filter((kind) => VIOLATION_MAP_POLICY_KINDS.includes(kind.id))

  const policyStatuses = Object.entries(enumsTranslationKeys.PolicyStatus).filter(
    ([id, _]) => id !== 'draft' && id !== 'upcoming' && id !== 'cancelled'
  )
  const statusEntries = getPolicyStatusEntries(policyStatuses)

  const providerEntries = getProviderEntries(props.providers, undefined, props.displayProviderColor)

  const policyKindEntries = getPolicyKindEntries(violationMapPolicyKinds, undefined, true)

  const policyEntries = props.policies
    // policies are sorted by published_date in policy_names end_point
    .map(({ id, name }) => ({
      id,
      label: name,
    }))
    .sort(sortObjectsByLabel)
  const deviceTypeEntries = getEnumEntries(agency_settings.vehicle_types, enumsTranslationKeys.DeviceType)

  function onFilterChange(key: string, newValue: any) {
    if (newValue === undefined) {
      console.error('Unable to apply undefined category filter on violation map')
      return
    }
    setFilters({ ...filters, [key]: newValue })
  }

  function onFromChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateRange', { to: filters.dateRange.to, from: value })
  }

  function onToChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateRange', { to: value, from: filters.dateRange.from })
  }

  function onBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    onFilterChange('dateRange', { from: valueFrom, to: valueTo })
  }

  return (
    <SidePanel
      headerProps={{
        title: t('filtersPanel.title.default'),
        information: props.violationsTruncated
          ? undefined
          : t('violationsWithCount', withFormattedCount(props.violationsTotalCount)),
        actions: props.violationsFailed ? (
          <Alert severity="error" sx={{ alignItems: 'center' }}>
            {t('violationMap.loadingFailed')}
          </Alert>
        ) : props.violationsTruncated ? (
          <Alert severity="warning" sx={{ alignItems: 'center' }}>
            {t('violationMap.truncatedWithCount', withFormattedCount(props.violationsTotalCount))}
            {`. ${t('virtualizedTable.useFiltersOrExport')}.`}
          </Alert>
        ) : undefined,
        onReset: () => setFilters(props.defaultFilters),
      }}
    >
      <CheckboxListFilter
        filterId={'policyStatus'}
        items={statusEntries}
        values={filters['policyStatus']}
        title={upperFirst(t('filtersPanel.policyStatus'))}
        onFilterChange={onFilterChange}
        displayCheckbox
        defaultExpanded
      />
      <CheckboxListFilter
        filterId={'provider'}
        items={providerEntries}
        values={filters['provider']}
        title={upperFirst(t('filtersPanel.provider'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'policyKind'}
        items={policyKindEntries}
        values={filters['policyKind']}
        title={upperFirst(t('policy.kind'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'policyId'}
        items={policyEntries}
        values={filters['policyId']}
        title={upperFirst(t('policy'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'deviceTypes'}
        items={deviceTypeEntries}
        values={filters['deviceTypes']}
        title={upperFirst(t('vehicles.category'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <TimeRangeFilterPro
        filterId={'dateRange'}
        format="L"
        values={{
          from: filters.dateRange.from,
          to: filters.dateRange.to,
        }}
        title={upperFirst(t('pricingFilter.violationDates'))}
        onFromChanged={onFromChanged}
        onToChanged={onToChanged}
        onBothChanged={onBothChanged}
        disableFuture
        defaultExpanded
      />
    </SidePanel>
  )
})

export default ViolationMapFilterPanel
